// Core/CardPainterManager.js
// Manages multiple CardPainterHost instances — open, close, paint.
// Mirrors JxWindowManager: this class owns the list of active cards,
// CardPainterHost stays the single-card renderer (resolve + render only).
// id = `${meta.type}:${meta.target}` — one card per (type, target), no duplicates.
import React from 'react';
import EngineCleanupBase from '../../../services/engines/EngineCleanupBase';
import cardPainterInstanceCache from './CardPainterInstanceCache';
import CardPainterHost from '../CardPainterHost/CardPainterHost';

const _name = 'CardPainterManager';

class CardPainterManager extends EngineCleanupBase {
    _painter = null;
    _booted = false;

    bootup = () => {
        if (this._booted) return;
        this._booted = true;
        this.initialize(_name);
    };

    /**
     * Registers the repaint callback. Called from the hosting component (e.g. on mount).
     * @param {function} repaint
     */
    registerPainter = (repaint) => {
        this._painter = repaint;
    };

    /**
     * Opens (or updates) a card instance. Same (type, target) updates in place, no duplicate.
     * @param {string} registryKey - e.g. 'simpleTicker:1'
     * @param {{type: string, ver: number, target: string|null}} meta
     * @param {object} props - additional props for the resolved painter
     */
    openCard = (registryKey, meta, props = {}) => {
        const id = `${meta.type}:${meta.target}`;
        cardPainterInstanceCache.addCard({ id, registryKey, meta, props });
        this._painter?.();
    };

    /**
     * Closes a card instance by id.
     * @param {string} id
     */
    closeCard = (id) => {
        cardPainterInstanceCache.removeCard(id);
        this._painter?.();
    };

    /**
     * Renders all open card instances.
     * @returns {React.ReactElement[]}
     */
    paint = () => {
        return cardPainterInstanceCache.getAll().map((inst) => (
            <CardPainterHost
                key={inst.rk}
                registryKey={inst.registryKey}
                meta={inst.meta}
                {...inst.props}
                open={true}
                onClose={() => this.closeCard(inst.rk)}
            />
        ));
    };

    clear() {
        super.clear();
        cardPainterInstanceCache.clear();
        this._booted = false;
        this._painter?.();
    }
}

const cardPainterManager = new CardPainterManager();
export default cardPainterManager;
